import { UserRole, ROLE_PERMISSIONS } from '../constants/enums';

export const hasPermission = (userRole: string | undefined, permission: keyof typeof ROLE_PERMISSIONS): boolean => {
  if (!userRole) return false;
  return ROLE_PERMISSIONS[permission].includes(userRole);
};

export const canApprove = (approverRole: UserRole, creatorRole: UserRole): boolean => {
  // Role hierarchy for approvals
  const approvalMatrix: Record<UserRole, UserRole[]> = {
    'admin': ['admin', 'director', 'floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],
    'director': ['director', 'floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],  
    'floor_manager': ['floor_manager', 'sales_team_lead', 'processor_team_lead', 'agent', 'processor_agent'],
    'sales_team_lead': ['sales_team_lead', 'agent'],
    'processor_team_lead': ['processor_team_lead', 'processor_agent'],
    'agent': [],
    'processor_agent': []
  };

  return approvalMatrix[approverRole]?.includes(creatorRole) || false;
};

export const getAvailableActionsForRole = (userRole: UserRole | undefined) => {
  if (!userRole) return [];

  const actions = [];
  
  if (hasPermission(userRole, 'canCreateSales')) {
    actions.push('create_sales');
  }
  
  if (hasPermission(userRole, 'canApproveSales')) {
    actions.push('approve_sales');
  }
  
  if (hasPermission(userRole, 'canProcessSales')) {
    actions.push('process_sales');
  }
  
  if (hasPermission(userRole, 'canManageUsers')) {
    actions.push('manage_users');
  }
  
  if (hasPermission(userRole, 'canViewAllTransactions')) {
    actions.push('view_all_transactions');
  }
  
  if (hasPermission(userRole, 'canRefund')) {
    actions.push('refund');
  }
  
  if (hasPermission(userRole, 'canVoid')) {
    actions.push('void');
  }
  
  if (hasPermission(userRole, 'canDecryptCardData')) {
    actions.push('decrypt_card_data');
  }
  
  if (hasPermission(userRole, 'canDecryptSSNOnly')) {
    actions.push('decrypt_ssn_only');
  }

  return actions;
};

export const getRoleDisplayName = (role: UserRole | string): string => {
  const roleNames: Record<UserRole, string> = {
    'agent': 'Sales Agent',
    'sales_team_lead': 'Sales Team Lead',
    'processor_agent': 'Processor Agent', 
    'processor_team_lead': 'Processor Team Lead',
    'floor_manager': 'Floor Manager',
    'director': 'Director',
    'admin': 'System Administrator'
  };

  return roleNames[role as UserRole] || role;
};

export const getRoleColor = (role: UserRole | string): string => {
  const roleColors: Record<UserRole, string> = {
    'agent': '#3498db',           // Blue
    'sales_team_lead': '#2ecc71', // Green
    'processor_agent': '#9b59b6', // Purple
    'processor_team_lead': '#8e44ad', // Dark Purple
    'floor_manager': '#e67e22',   // Orange
    'director': '#e74c3c',        // Red
    'admin': '#34495e'            // Dark Blue Grey
  };

  return roleColors[role as UserRole] || '#95a5a6';
};